import { type Locale, type MessageKey, TRANSLATED_LOCALES } from "@colorglo/i18n";
import { t as translate } from "@colorglo/i18n/catalogs/staff";
import { setTheme, THEMES, type Theme, useTheme } from "@colorglo/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarRail,
  SidebarSeparator,
} from "@colorglo/ui/components";
import {
  ActionImpersonate,
  ActionPassword,
  ActionSelect,
  ActionSignOut,
  IndicatorCheck,
  iconSize,
  NavLanguage,
  NavSettings,
  NavTheme,
} from "@colorglo/ui/icons";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useSignOut } from "../api/auth.ts";
import { useMe, useSetLocale } from "../api/hooks.ts";
import { useStaffNotifications } from "../api/staff-notifications.ts";
import { useLocale, useT } from "../lib/locale.ts";
import {
  MAIN,
  NETWORK,
  NOTIFICATIONS,
  SETTINGS_ROOT,
  SHOP,
  SHOP_ORDERS,
  SHOP_PRODUCTS,
} from "../lib/nav.ts";
import { ChangePasswordDialog } from "./change-password-dialog.tsx";

export function AppSidebar({
  franchiseId,
  onFranchiseChange,
}: {
  readonly franchiseId: string | null;
  readonly onFranchiseChange: (id: string) => void;
}) {
  const t = useT();
  const me = useMe();
  const { pathname } = useLocation();

  const franchises = me.data?.franchises ?? [];
  const selected = franchiseId ?? franchises[0]?.id ?? "";
  const current = franchises.find((franchise) => franchise.id === selected);
  const hasNetwork = (me.data?.network.countries.length ?? 0) > 0;
  const canSeeNotifications = franchises
    .find((entry) => entry.id === selected)
    ?.permissions.includes("staff_notification:read");
  const canShop = current?.permissions.includes("shop:browse") ?? false;
  const canManageOrders = (me.data?.network_permissions ?? []).includes("shop_order:manage");
  const canManageProducts = (me.data?.network_permissions ?? []).includes("product:manage");

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="gap-2">
        {/*
          At 48px the wordmark renders about thirteen pixels wide, which is a smudge rather
          than a mark. The workspace tile below carries the identity on the rail instead.
        */}
        <Link
          to="/"
          className="flex items-center gap-2.5 px-2 py-1 group-data-[collapsible=icon]:hidden"
        >
          <BrandMark
            key={current?.logo_url ?? ""}
            logoUrl={current?.logo_url ?? null}
            name={current?.name ?? ""}
          />
        </Link>

        <SidebarMenu>
          <SidebarMenuItem>
            <WorkspaceSwitcher
              franchises={franchises}
              selected={selected}
              loading={me.data === undefined}
              onSelect={onFranchiseChange}
            />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {MAIN.map((item) => (
                <SidebarMenuItem key={item.to}>
                  <SidebarMenuButton
                    asChild
                    isActive={pathname === item.to}
                    tooltip={t(item.label)}
                  >
                    <Link to={item.to}>
                      <item.icon {...iconSize.control} aria-hidden="true" />
                      <span>{t(item.label)}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}

              {canSeeNotifications && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={pathname.startsWith(NOTIFICATIONS.to)}
                    tooltip={t(NOTIFICATIONS.label)}
                  >
                    <Link to={NOTIFICATIONS.to}>
                      <NOTIFICATIONS.icon {...iconSize.control} aria-hidden="true" />
                      <span>{t(NOTIFICATIONS.label)}</span>
                      <UnreadBadge franchiseId={selected} />
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {canShop && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={pathname.startsWith(SHOP.to)}
                    tooltip={t(SHOP.label)}
                  >
                    <Link to={SHOP.to}>
                      <SHOP.icon {...iconSize.control} aria-hidden="true" />
                      <span>{t(SHOP.label)}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {hasNetwork && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={pathname.startsWith(NETWORK.to)}
                    tooltip={t(NETWORK.label)}
                  >
                    <Link to={NETWORK.to} search={{ vy: "oversikt" }}>
                      <NETWORK.icon {...iconSize.control} aria-hidden="true" />
                      <span>{t(NETWORK.label)}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {canManageOrders && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={pathname.startsWith(SHOP_ORDERS.to)}
                    tooltip={t(SHOP_ORDERS.label)}
                  >
                    <Link to={SHOP_ORDERS.to}>
                      <SHOP_ORDERS.icon {...iconSize.control} aria-hidden="true" />
                      <span>{t(SHOP_ORDERS.label)}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {canManageProducts && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={pathname.startsWith(SHOP_PRODUCTS.to)}
                    tooltip={t(SHOP_PRODUCTS.label)}
                  >
                    <Link to={SHOP_PRODUCTS.to}>
                      <SHOP_PRODUCTS.icon {...iconSize.control} aria-hidden="true" />
                      <span>{t(SHOP_PRODUCTS.label)}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator className="group-data-[collapsible=icon]:mx-1" />
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={pathname.startsWith(SETTINGS_ROOT)}
                  tooltip={t("settings.title")}
                >
                  <Link to={SETTINGS_ROOT} search={{ franchise: selected }}>
                    <NavSettings {...iconSize.control} aria-hidden="true" />
                    <span>{t("settings.title")}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarSeparator className="group-data-[collapsible=icon]:mx-1" />
        <AccountMenu />
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}

/**
 * The unread count, on the sidebar row.
 *
 * Reads the bell's own query rather than fetching again — the header mounted it, so this is
 * a cache hit and the two counts cannot disagree. `SidebarMenuBadge` positions itself and
 * hides on the 48px rail, where the header's bell is still visible and carries the same
 * number.
 */
function UnreadBadge({ franchiseId }: { readonly franchiseId: string }) {
  const feed = useStaffNotifications(franchiseId, franchiseId !== "");
  const unread = feed.data?.unread_count ?? 0;

  if (unread === 0) return null;
  return <SidebarMenuBadge>{unread > 99 ? "99+" : unread}</SidebarMenuBadge>;
}

function BrandMark({ logoUrl, name }: { readonly logoUrl: string | null; readonly name: string }) {
  const [failed, setFailed] = useState(false);
  const theme = useTheme();

  if (logoUrl === null || logoUrl === "" || failed) {
    return (
      <img
        src={theme === "dark" ? "/logo/colorglo-logo-light.svg" : "/logo/colorglo.svg"}
        alt={translate("app.title")}
        className="h-12 w-auto max-w-44"
        width={2643}
        height={1011}
      />
    );
  }

  return (
    <img
      src={logoUrl}
      alt={name}
      className="h-6 w-auto max-w-40 object-contain"
      onError={() => setFailed(true)}
    />
  );
}

/** The square that survives the icon rail. Two letters, because "Malmö" and "Mölndal" exist. */
function Tile({ from, className }: { readonly from: string; readonly className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`flex size-8 shrink-0 items-center justify-center rounded-md bg-sidebar-accent text-caption font-medium tracking-wide text-sidebar-accent-foreground ${className ?? ""}`}
    >
      {from.trim().slice(0, 2).toUpperCase()}
    </span>
  );
}

/**
 * Which workshop everything on screen is scoped to.
 *
 * It was a bare `<Select>` under the wordmark with `group-data-[collapsible=icon]:hidden` on
 * it, so collapsing the sidebar removed the only indication of which of twenty-six locations
 * you were about to edit. The tile is what makes it survive the rail.
 *
 * Someone with exactly one franchise gets the same block without the chevron rather than no
 * block at all — the answer to "where am I" is worth a row even when it cannot change.
 */
function WorkspaceSwitcher({
  franchises,
  selected,
  loading,
  onSelect,
}: {
  readonly franchises: readonly { id: string; name: string; country_code: string }[];
  readonly selected: string;
  readonly loading: boolean;
  readonly onSelect: (id: string) => void;
}) {
  const t = useT();

  if (loading) return <SidebarMenuSkeleton showIcon className="h-12" />;

  const current = franchises.find((franchise) => franchise.id === selected) ?? franchises[0];
  if (current === undefined) return null;

  const body = (
    <>
      <Tile from={current.name} />
      <span className="grid min-w-0 flex-1 text-left leading-tight">
        <span className="truncate font-medium">{current.name}</span>
        <span className="truncate text-caption text-sidebar-foreground/60">
          {t("settings.workshop")} · {current.country_code}
        </span>
      </span>
    </>
  );

  if (franchises.length < 2) {
    return (
      <SidebarMenuButton size="lg" tooltip={current.name} className="pointer-events-none">
        {body}
      </SidebarMenuButton>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton size="lg" aria-label={t("settings.workshop")} tooltip={current.name}>
          {body}
          <ActionSelect {...iconSize.inline} aria-hidden="true" className="ml-auto shrink-0" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="right"
        sideOffset={8}
        className="max-h-[60svh] w-64 overflow-y-auto"
      >
        <DropdownMenuLabel>{t("settings.workshop")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {franchises.map((franchise) => (
          <DropdownMenuItem
            key={franchise.id}
            onSelect={() => onSelect(franchise.id)}
            className="gap-2"
          >
            <span className="truncate">{franchise.name}</span>
            {franchise.id === current.id && (
              <IndicatorCheck {...iconSize.inline} aria-hidden="true" className="ml-auto" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Who is signed in, and the way out.
 *
 * Sign-out was a bare row at the bottom and the signed-in identity appeared nowhere unless
 * you happened to be impersonating — so on a shared workshop machine there was no way to
 * check whose account you were about to book under.
 */
function AccountMenu() {
  const t = useT();
  const me = useMe();
  const navigate = useNavigate();
  const signOut = useSignOut();
  const [changingPassword, setChangingPassword] = useState(false);

  if (me.data === undefined) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuSkeleton showIcon className="h-12" />
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  const { user, impersonation } = me.data;
  const canChangePassword =
    impersonation == null && me.data.network_permissions.includes("owner_account:manage");

  return (
    <SidebarMenu>
      {impersonation != null && (
        <SidebarMenuItem>
          <SidebarMenuButton
            size="sm"
            tooltip={t("nav.impersonating", { email: user.email })}
            className="bg-warning-subtle text-warning-on hover:bg-warning-subtle"
          >
            <ActionImpersonate {...iconSize.inline} aria-hidden="true" />
            <span className="truncate">{user.email}</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      )}

      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton size="lg" aria-label={t("nav.account")} tooltip={user.name}>
              <Tile from={user.name} className="rounded-full" />
              <span className="grid min-w-0 flex-1 text-left leading-tight">
                <span className="truncate font-medium">{user.name}</span>
                <span className="truncate text-caption text-sidebar-foreground/60">
                  {user.email}
                </span>
              </span>
              <ActionSelect {...iconSize.inline} aria-hidden="true" className="ml-auto shrink-0" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="right" sideOffset={8} className="w-64">
            <DropdownMenuLabel className="flex items-center gap-2 font-normal">
              <Tile from={user.name} className="rounded-full" />
              <span className="grid min-w-0 leading-tight">
                <span className="truncate font-medium">{user.name}</span>
                <span className="truncate text-caption text-muted-foreground">{user.email}</span>
              </span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <LanguageSubmenu />
            <ThemeSubmenu />
            <DropdownMenuSeparator />
            {canChangePassword && (
              <DropdownMenuItem onSelect={() => setChangingPassword(true)}>
                <ActionPassword {...iconSize.inline} aria-hidden="true" />
                {t("auth.change_password.title")}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              disabled={signOut.isPending}
              onSelect={() =>
                signOut.mutate(undefined, {
                  onSettled: () => void navigate({ to: "/logga-in" }),
                })
              }
            >
              <ActionSignOut {...iconSize.inline} aria-hidden="true" />
              {t("auth.sign_out")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {canChangePassword && (
          <ChangePasswordDialog open={changingPassword} onOpenChange={setChangingPassword} />
        )}
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

/**
 * The user's own language, in the account menu.
 *
 * Here rather than on a settings screen because `installningar/*` is the *franchise's*
 * configuration — an owner's form, edited twice a year — and this is a property of the person
 * signed in. A technician on a shared workshop tablet needs it where their own name is.
 *
 * Only the translated locales are offered. `da` and `fi` exist in `LOCALES` and fall through
 * to English, and a menu item that silently does nothing is worse than one that is absent.
 *
 * The languages are named in themselves — Svenska, Norsk, English — so the list is readable
 * from inside whichever one you are currently trying to leave.
 */
function LanguageSubmenu() {
  const t = useT();
  const locale = useLocale();
  const setLocale = useSetLocale();

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <NavLanguage {...iconSize.inline} aria-hidden="true" />
        {t("nav.language")}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        <DropdownMenuRadioGroup
          value={locale}
          onValueChange={(next) => setLocale.mutate(next as Locale)}
        >
          {TRANSLATED_LOCALES.map((code) => (
            <DropdownMenuRadioItem key={code} value={code} disabled={setLocale.isPending}>
              {t(`language.${code}` as MessageKey)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

/**
 * Light or dark, in the same menu as the language.
 *
 * Not on a settings screen and not stored on the account: `installningar/*` is the franchise's
 * configuration, and the theme is a property of the device rather than the person — the same
 * technician wants dark on their phone at night and light on the workshop tablet in a bright
 * bay. `setTheme` writes the class and `localStorage`; nothing round-trips to the API.
 */
function ThemeSubmenu() {
  const t = useT();
  const theme = useTheme();

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <NavTheme {...iconSize.inline} aria-hidden="true" />
        {t("nav.theme")}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        <DropdownMenuRadioGroup value={theme} onValueChange={(next) => setTheme(next as Theme)}>
          {THEMES.map((name) => (
            <DropdownMenuRadioItem key={name} value={name}>
              {t(`theme.${name}` as MessageKey)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
